'use client'

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'

import { MoodPostCard, type PostCardData } from '@/components/blog/mood-post-card'

/** Posts revealed per scroll batch — matches the mood grid so rows stay full. */
const BATCH_SIZE = 12

/**
 * The all-stories view's post listing (blog FR-13b): the whole corpus as a card
 * grid grouped under month-and-year headings, newest first, revealed in
 * {@link BATCH_SIZE}-post batches as the reader scrolls — the mood grid's
 * reveal pattern over a grouped sequence. Grouping and reveal are display only:
 * SEO coverage is the FR-13a server-rendered index, so this view carries no
 * link-equity burden and client-side slicing is fine here (unlike there).
 *
 * A group's heading renders only once its first card is revealed, so no month
 * dangles over an empty grid while the reader hasn't scrolled to it yet.
 */
export function AllStoriesGrid({ posts }: { posts: PostCardData[] }) {
    const t = useTranslations('blogHome.wanderByMood')
    const [visibleCount, setVisibleCount] = useState(Math.min(BATCH_SIZE, posts.length))
    const sentinelRef = useRef<HTMLDivElement | null>(null)
    const allVisible = visibleCount >= posts.length

    useEffect(() => {
        const sentinel = sentinelRef.current
        if (!sentinel) {
            return // every post already visible — nothing to observe
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setVisibleCount((count) => Math.min(count + BATCH_SIZE, posts.length))
                }
            },
            // Pre-trigger just before the reader reaches the bottom (same margin
            // as the mood grid).
            { rootMargin: '200px 0px' },
        )
        observer.observe(sentinel)
        return () => observer.disconnect()
    }, [allVisible, posts.length])

    return (
        <div>
            <p aria-live="polite" className="sr-only">
                {t('showingCount', { visible: visibleCount, total: posts.length })}
            </p>
            {groupByMonth(posts.slice(0, visibleCount)).map(([month, monthPosts]) => (
                <section key={month} className="mt-10 first:mt-0">
                    {/* Editorial rule: the hairline runs from the heading to the
                        margin, giving each month a visible shelf edge. */}
                    <div className="mb-5 flex items-baseline gap-4">
                        <h2 className="text-xl font-bold tracking-tight text-text-primary">
                            {formatMonthHeading(month)}
                        </h2>
                        <span aria-hidden="true" className="h-px flex-1 self-center bg-border" />
                    </div>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {monthPosts.map((post) => (
                            <MoodPostCard key={post.slug} post={post} />
                        ))}
                    </div>
                </section>
            ))}
            {allVisible ? null : <div ref={sentinelRef} aria-hidden="true" className="h-px" />}
        </div>
    )
}

/**
 * Posts bucketed by publication month, in arrival order (the inventory is
 * already newest-first). The key is the `publishedAt` string's own `YYYY-MM` —
 * the timestamp carries its offset, so its leading characters are the
 * publication month identically on server and client
 * (`invariants.md#ssr-safe-rendered-dates`).
 */
function groupByMonth(posts: PostCardData[]): [string, PostCardData[]][] {
    const byMonth = new Map<string, PostCardData[]>()
    for (const post of posts) {
        const month = post.publishedAt.slice(0, 7)
        const bucket = byMonth.get(month)
        if (bucket) {
            bucket.push(post)
        } else {
            byMonth.set(month, [post])
        }
    }
    return [...byMonth.entries()]
}

/**
 * "August 2026" from a `YYYY-MM` key: a `Date` built from explicit Y/M parts at
 * local noon of the 1st, formatted in that same local zone — deterministic per
 * input in every timezone, the `formatShortDate` pattern. Month names come from
 * `Intl`, never a maintained list (`invariants.md#facts-canonical-store-editorial`).
 */
function formatMonthHeading(month: string): string {
    const [year, monthIndex] = month.split('-').map(Number)
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(
        new Date(year ?? 0, (monthIndex ?? 1) - 1, 1, 12),
    )
}
